import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all locale files statically for offline support
import en from '@/locales/en.json';

// Languages will be loaded dynamically but the detected/saved one is eager
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'es', name: 'Español', dir: 'ltr' },
  { code: 'fr', name: 'Français', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', dir: 'ltr' },
  { code: 'it', name: 'Italiano', dir: 'ltr' },
  { code: 'pt', name: 'Português', dir: 'ltr' },
  { code: 'nl', name: 'Nederlands', dir: 'ltr' },
  { code: 'pl', name: 'Polski', dir: 'ltr' },
  { code: 'uk', name: 'Українська', dir: 'ltr' },
  { code: 'ru', name: 'Русский', dir: 'ltr' },
  { code: 'cs', name: 'Čeština', dir: 'ltr' },
  { code: 'ro', name: 'Română', dir: 'ltr' },
  { code: 'hu', name: 'Magyar', dir: 'ltr' },
  { code: 'sv', name: 'Svenska', dir: 'ltr' },
  { code: 'no', name: 'Norsk', dir: 'ltr' },
  { code: 'da', name: 'Dansk', dir: 'ltr' },
  { code: 'fi', name: 'Suomi', dir: 'ltr' },
  { code: 'el', name: 'Ελληνικά', dir: 'ltr' },
  { code: 'tr', name: 'Türkçe', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' },
  { code: 'he', name: 'עברית', dir: 'rtl' },
  { code: 'fa', name: 'فارسی', dir: 'rtl' },
  { code: 'hi', name: 'हिन्दी', dir: 'ltr' },
  { code: 'bn', name: 'বাংলা', dir: 'ltr' },
  { code: 'ta', name: 'தமிழ்', dir: 'ltr' },
  { code: 'te', name: 'తెలుగు', dir: 'ltr' },
  { code: 'ur', name: 'اردو', dir: 'rtl' },
  { code: 'zh', name: '中文(简体)', dir: 'ltr' },
  { code: 'zh-TW', name: '中文(繁體)', dir: 'ltr' },
  { code: 'ja', name: '日本語', dir: 'ltr' },
  { code: 'ko', name: '한국어', dir: 'ltr' },
  { code: 'th', name: 'ไทย', dir: 'ltr' },
  { code: 'vi', name: 'Tiếng Việt', dir: 'ltr' },
  { code: 'id', name: 'Bahasa Indonesia', dir: 'ltr' },
  { code: 'ms', name: 'Bahasa Melayu', dir: 'ltr' },
  { code: 'fil', name: 'Filipino', dir: 'ltr' },
  { code: 'my', name: 'မြန်မာ', dir: 'ltr' },
  { code: 'km', name: 'ខ្មែរ', dir: 'ltr' },
  { code: 'sw', name: 'Kiswahili', dir: 'ltr' },
  { code: 'am', name: 'አማርኛ', dir: 'ltr' },
  { code: 'bg', name: 'Български', dir: 'ltr' },
  { code: 'hr', name: 'Hrvatski', dir: 'ltr' },
  { code: 'sk', name: 'Slovenčina', dir: 'ltr' },
  { code: 'sl', name: 'Slovenščina', dir: 'ltr' },
  { code: 'sr', name: 'Srpski', dir: 'ltr' },
  { code: 'lt', name: 'Lietuvių', dir: 'ltr' },
  { code: 'lv', name: 'Latviešu', dir: 'ltr' },
  { code: 'et', name: 'Eesti', dir: 'ltr' },
  { code: 'ka', name: 'ქართული', dir: 'ltr' },
  { code: 'az', name: 'Azərbaycan', dir: 'ltr' },
  { code: 'kk', name: 'Қазақ', dir: 'ltr' },
  { code: 'uz', name: 'Oʻzbek', dir: 'ltr' },
  { code: 'ne', name: 'नेपाली', dir: 'ltr' },
  { code: 'si', name: 'සිංහල', dir: 'ltr' },
  { code: 'lo', name: 'ລາວ', dir: 'ltr' },
  { code: 'mn', name: 'Монгол', dir: 'ltr' },
  { code: 'af', name: 'Afrikaans', dir: 'ltr' },
  { code: 'ca', name: 'Català', dir: 'ltr' },
  { code: 'eu', name: 'Euskara', dir: 'ltr' },
  { code: 'gl', name: 'Galego', dir: 'ltr' },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

export { SUPPORTED_LANGUAGES };

// RTL languages
const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

export function isRTL(lang: string): boolean {
  return RTL_LANGUAGES.has(lang);
}

/** Apply text direction to HTML element */
function applyDirection(lng: string) {
  const dir = isRTL(lng) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lng);
}

// Lazy-load locale files for non-English languages
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import('@/locales/es.json'),
  fr: () => import('@/locales/fr.json'),
  de: () => import('@/locales/de.json'),
  it: () => import('@/locales/it.json'),
  pt: () => import('@/locales/pt.json'),
  nl: () => import('@/locales/nl.json'),
  pl: () => import('@/locales/pl.json'),
  uk: () => import('@/locales/uk.json'),
  ru: () => import('@/locales/ru.json'),
  cs: () => import('@/locales/cs.json'),
  ro: () => import('@/locales/ro.json'),
  hu: () => import('@/locales/hu.json'),
  sv: () => import('@/locales/sv.json'),
  no: () => import('@/locales/no.json'),
  da: () => import('@/locales/da.json'),
  fi: () => import('@/locales/fi.json'),
  el: () => import('@/locales/el.json'),
  tr: () => import('@/locales/tr.json'),
  ar: () => import('@/locales/ar.json'),
  he: () => import('@/locales/he.json'),
  fa: () => import('@/locales/fa.json'),
  hi: () => import('@/locales/hi.json'),
  bn: () => import('@/locales/bn.json'),
  ta: () => import('@/locales/ta.json'),
  te: () => import('@/locales/te.json'),
  ur: () => import('@/locales/ur.json'),
  zh: () => import('@/locales/zh.json'),
  'zh-TW': () => import('@/locales/zh-TW.json'),
  ja: () => import('@/locales/ja.json'),
  ko: () => import('@/locales/ko.json'),
  th: () => import('@/locales/th.json'),
  vi: () => import('@/locales/vi.json'),
  id: () => import('@/locales/id.json'),
  ms: () => import('@/locales/ms.json'),
  fil: () => import('@/locales/fil.json'),
  my: () => import('@/locales/my.json'),
  km: () => import('@/locales/km.json'),
  sw: () => import('@/locales/sw.json'),
  am: () => import('@/locales/am.json'),
  bg: () => import('@/locales/bg.json'),
  hr: () => import('@/locales/hr.json'),
  sk: () => import('@/locales/sk.json'),
  sl: () => import('@/locales/sl.json'),
  sr: () => import('@/locales/sr.json'),
  lt: () => import('@/locales/lt.json'),
  lv: () => import('@/locales/lv.json'),
  et: () => import('@/locales/et.json'),
  ka: () => import('@/locales/ka.json'),
  az: () => import('@/locales/az.json'),
  kk: () => import('@/locales/kk.json'),
  uz: () => import('@/locales/uz.json'),
  ne: () => import('@/locales/ne.json'),
  si: () => import('@/locales/si.json'),
  lo: () => import('@/locales/lo.json'),
  mn: () => import('@/locales/mn.json'),
  af: () => import('@/locales/af.json'),
  ca: () => import('@/locales/ca.json'),
  eu: () => import('@/locales/eu.json'),
  gl: () => import('@/locales/gl.json'),
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map(l => l.code),
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'aphenis_language',
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
    },
  });

// Apply direction on init
applyDirection(i18n.language || 'en');

// Load locale bundle on language change
i18n.on('languageChanged', async (lng) => {
  applyDirection(lng);
  
  // If the language is already loaded, skip
  if (i18n.hasResourceBundle(lng, 'translation')) return;

  const loader = localeLoaders[lng];
  if (loader) {
    try {
      const mod = await loader();
      i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
    } catch (e) {
      console.warn(`[i18n] Failed to load locale ${lng}, falling back to English`, e);
    }
  }
});

// If the detected language is not English, eagerly load it
const detectedLang = i18n.language;
if (detectedLang && detectedLang !== 'en' && localeLoaders[detectedLang]) {
  localeLoaders[detectedLang]().then(mod => {
    i18n.addResourceBundle(detectedLang, 'translation', mod.default, true, true);
  }).catch(() => {});
}

export default i18n;
